import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { setCredentials } from '../slices/authSlice';

// Use absolute API base when available (Amplify should set VITE_API_BASE_URL)
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
const apiUrl = (path) => (API_BASE ? `${API_BASE}${path}` : path);

const InvitationScreen = () => {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchInvitation();
  }, [inviteId]);

  const fetchInvitation = async () => {
    try {
      const response = await fetch(apiUrl(`/api/invites/invite/${inviteId}`), {
        credentials: 'include'
      });
      
      if (!response.ok) {
        // Handle non-200 responses
        let errorMessage = 'Failed to load invitation';
        
        if (response.status === 404) {
          errorMessage = '🔗 This invitation link is invalid or has expired. Please check the link or contact the person who invited you.';
        } else if (response.status === 400) {
          const data = await response.json().catch(() => ({}));
          if (data.message?.includes('responded to') || data.message?.includes('already been')) {
            errorMessage = '✅ This invitation has already been used. If you need a new invitation, please contact the league administrator.';
          } else {
            errorMessage = `❌ ${data.message || 'Invalid invitation request'}`;
          }
        } else if (response.status === 500) {
          errorMessage = '🔧 Server error occurred. Please try again in a few minutes or contact support if the problem persists.';
        } else if (response.status === 403) {
          errorMessage = '🚫 Access denied. This invitation may be restricted or expired.';
        } else {
          // Try to get error message from response
          const data = await response.json().catch(() => ({}));
          errorMessage = `⚠️ ${data.message || `Server returned error ${response.status}. Please try again later.`}`;
        }
        
        setError(errorMessage);
        return;
      }

      const data = await response.json();
      setInvitation(data.invitation);
      setFormData(prev => ({ ...prev, email: data.invitation.invitedEmail }));
      
    } catch (err) {
      console.error('Error fetching invitation:', err);
      
      // Provide more specific error messages based on error type
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('🌐 Unable to connect to the server. Please check your internet connection and try again.');
      } else if (err.name === 'SyntaxError') {
        setError('📄 Received invalid response from server. Please try refreshing the page.');
      } else if (err.message.includes('NetworkError') || err.message.includes('Failed to fetch')) {
        setError('🔌 Network connection failed. Please check your internet connection and try again.');
      } else {
        setError('❌ An unexpected error occurred while loading the invitation. Please try refreshing the page or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();
    setAccepting(true);
    setError('');

    // Validate password confirmation if showing registration form
    if (showRegistrationForm && formData.password !== formData.confirmPassword) {
      setError('🔐 Passwords do not match. Please make sure both password fields are identical.');
      setAccepting(false);
      return;
    }

    // Validate password strength for new users
    if (showRegistrationForm && formData.password && formData.password.length < 6) {
      setError('🔒 Password must be at least 6 characters long.');
      setAccepting(false);
      return;
    }

    // Validate username for new users
    if (showRegistrationForm && formData.username && formData.username.length < 3) {
      setError('👤 Username must be at least 3 characters long.');
      setAccepting(false);
      return;
    }

    try {
      const response = await fetch(apiUrl(`/api/invites/invite/${inviteId}/accept`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          username: formData.username || undefined,
          password: formData.password || undefined,
        }),
      });

      if (!response.ok) {
        // Handle non-200 responses with specific error messages
        let errorMessage = 'Failed to accept invitation';
        
        if (response.status === 404) {
          errorMessage = '🔗 This invitation is no longer valid. It may have been removed or expired.';
        } else if (response.status === 400) {
          const data = await response.json().catch(() => ({}));
          if (data.requiresRegistration) {
            setShowRegistrationForm(true);
            setError('📝 Please complete your registration to join the league');
            setAccepting(false);
            return;
          } else if (data.message?.includes('already')) {
            errorMessage = '✅ This invitation has already been accepted. You may already be a member of this league.';
          } else if (data.message?.includes('password')) {
            errorMessage = '🔐 Password requirements not met. Please ensure your password is at least 6 characters long.';
          } else if (data.message?.includes('username')) {
            errorMessage = '👤 Username is already taken or invalid. Please choose a different username.';
          } else if (data.message?.includes('email')) {
            errorMessage = '📧 Email address issue. The email may already be registered with another account.';
          } else {
            errorMessage = `❌ ${data.message || 'Invalid request. Please check your information and try again.'}`;
          }
        } else if (response.status === 403) {
          errorMessage = '🚫 Access denied. This invitation may not be for your email address.';
        } else if (response.status === 409) {
          errorMessage = '👥 You are already a member of this league or the invitation conflicts with existing data.';
        } else if (response.status === 500) {
          errorMessage = '🔧 Server error occurred while processing your request. Please try again in a few minutes.';
        } else {
          const data = await response.json().catch(() => ({}));
          errorMessage = `⚠️ ${data.message || `Server error (${response.status}). Please try again later.`}`;
        }
        
        setError(errorMessage);
        setAccepting(false);
        return;
      }

      const data = await response.json();
      
      // Success handling
      toast.success(`🎉 Successfully joined ${invitation.leagueName}!`);
      if (data.isNewUser) {
        toast.info('👋 Welcome! Your account has been created successfully.');
      }
      
      // Redirect to login or leagues page
      dispatch(setCredentials({ userInfo: data }));
      navigate(data.redirectPath || '/leagues');
      
    } catch (err) {
      console.error('Error accepting invitation:', err);
      
      // Provide specific error messages based on error type
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('🌐 Unable to connect to the server. Please check your internet connection and try again.');
      } else if (err.name === 'SyntaxError') {
        setError('📄 Received invalid response from server. Please try refreshing the page and trying again.');
      } else if (err.message.includes('NetworkError') || err.message.includes('Failed to fetch')) {
        setError('🔌 Network connection failed. Please check your internet connection and try again.');
      } else {
        setError('❌ An unexpected error occurred while accepting the invitation. Please try again or contact support.');
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Card className="mx-auto" style={{ maxWidth: '400px' }}>
          <Card.Body>
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <h5>Loading invitation...</h5>
            <p className="text-muted mb-0">
              Please wait while we verify your invitation link.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (error && !invitation) {
    return (
      <Container className="mt-5">
        <Card className="mx-auto" style={{ maxWidth: '600px' }}>
          <Card.Body className="text-center">
            <Card.Title className="text-danger mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Invitation Issue
            </Card.Title>
            <Card.Text className="mb-4 text-start">
              {error}
            </Card.Text>
            
            {/* Helpful suggestions based on error type */}
            <div className="alert alert-info text-start mb-4">
              <h6><i className="fas fa-lightbulb me-2"></i>What to try:</h6>
              <ul className="mb-0">
                {error.includes('invalid') || error.includes('expired') ? (
                  <>
                    <li>Check that you copied the complete invitation link</li>
                    <li>Ask for a new invitation if this one is old</li>
                    <li>Contact the league administrator for help</li>
                  </>
                ) : error.includes('already') ? (
                  <>
                    <li>Try logging in to your existing account</li>
                    <li>Check if you're already a member of this league</li>
                    <li>Contact the league administrator if you need access</li>
                  </>
                ) : error.includes('connect') || error.includes('Network') ? (
                  <>
                    <li>Check your internet connection</li>
                    <li>Try refreshing the page</li>
                    <li>Try again in a few minutes</li>
                  </>
                ) : (
                  <>
                    <li>Try refreshing the page</li>
                    <li>Check your internet connection</li>
                    <li>Contact support if the problem continues</li>
                  </>
                )}
              </ul>
            </div>
            
            <div className="d-grid gap-2">
              <Button variant="primary" onClick={() => window.location.reload()}>
                <i className="fas fa-redo me-2"></i>
                Try Again
              </Button>
              <Button variant="outline-primary" onClick={() => navigate('/login')}>
                <i className="fas fa-sign-in-alt me-2"></i>
                Go to Login
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate('/')}>
                <i className="fas fa-home me-2"></i>
                Go to Home
              </Button>
            </div>
            
            <div className="mt-4 pt-3 border-top">
              <small className="text-muted">
                <i className="fas fa-envelope me-1"></i>
                Need help? Contact the person who sent you this invitation or reach out to support.
              </small>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Card className="mx-auto" style={{ maxWidth: '600px' }}>
        <Card.Body>
          <Card.Title className="text-center mb-4">
            League Invitation
          </Card.Title>
          
          {invitation && (
            <>
              <div className="text-center mb-4">
                <h4>You're invited to join:</h4>
                <h3 className="text-primary">{invitation.leagueName}</h3>
                {invitation.leagueDescription && (
                  <p className="text-muted">{invitation.leagueDescription}</p>
                )}
                <p>
                  Invited by: <strong>{invitation.sentBy?.username || invitation.sentBy?.email}</strong>
                </p>
                <small className="text-muted">
                  Sent on {new Date(invitation.createdAt).toLocaleDateString()}
                </small>
              </div>

              {error && (
                <Alert variant="danger" className="mb-3">
                  <div className="d-flex align-items-start">
                    <i className="fas fa-exclamation-circle me-2 mt-1"></i>
                    <div>
                      <div className="fw-bold mb-1">Something went wrong:</div>
                      <div>{error}</div>
                      {error.includes('connect') || error.includes('Network') && (
                        <small className="d-block mt-2 text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          This is usually a temporary connection issue. Please try again.
                        </small>
                      )}
                    </div>
                  </div>
                </Alert>
              )}

              <Form onSubmit={handleAcceptInvitation}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled
                  />
                  <Form.Text className="text-muted">
                    This invitation is for this email address only.
                  </Form.Text>
                </Form.Group>

                {showRegistrationForm && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        placeholder="Choose a username"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Create a password"
                        required
                        minLength={6}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Confirm Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirm your password"
                        required
                        minLength={6}
                      />
                    </Form.Group>
                  </>
                )}

                <div className="d-grid gap-2">
                  <Button
                    variant="success"
                    type="submit"
                    size="lg"
                    disabled={accepting}
                  >
                    {accepting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        {showRegistrationForm ? 'Creating Account & Joining League...' : 'Accepting Invitation...'}
                      </>
                    ) : (
                      <>
                        <i className={`fas ${showRegistrationForm ? 'fa-user-plus' : 'fa-check'} me-2`}></i>
                        {showRegistrationForm ? 'Create Account & Join League' : 'Accept Invitation'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>

              <div className="text-center mt-3">
                <small className="text-muted">
                  Already have an account? <a href="/login">Sign in first</a> to join this league with your existing profile.
                </small>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default InvitationScreen;
